import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Plus, LayoutList, Layers, Rows3 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { backendUrl, getStageLabel } from './gameplanUtils';
import AddDealDialog from './AddDealDialog';
import ImportCsvDialog from './ImportCsvDialog';
import WeekColumn from './WeekColumn';
import PremiumBoard from './PremiumBoard';

const DENSITY_KEY = 'gp-board-density';
const DENSITY_OPTIONS = [
  { key: 'compact', label: 'Compact', icon: LayoutList },
  { key: 'comfortable', label: 'Comfortable', icon: Layers },
  { key: 'expanded', label: 'Expanded', icon: Rows3 },
];

const BoardView = ({ month, boardData, live, readOnly = false, onUpdateDeal, onRefresh, options, authToken, onOpenFile, onEditDeal, onStage, boardOnly = false }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [presetWeek, setPresetWeek] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'executive';
    return window.localStorage.getItem('gp-view-mode') || 'executive';
  });
  useEffect(() => { try { window.localStorage.setItem('gp-view-mode', viewMode); } catch (_) { /* noop */ } }, [viewMode]);
  const [density, setDensity] = useState(() => {
    if (typeof window === 'undefined') return 'comfortable';
    return window.localStorage.getItem(DENSITY_KEY) || 'comfortable';
  });
  const headers = { 'Authorization': `Bearer ${authToken}` };

  useEffect(() => {
    try { window.localStorage.setItem(DENSITY_KEY, density); } catch (_) { /* noop */ }
  }, [density]);

  // Pull the "This Week" detailed view (Mon→Fri) and surface it directly
  // above the Weekly Pipeline Board. The card refreshes whenever boardData
  // changes (i.e. after add/edit/delete deal) so WTD totals stay live.

  // Handle dropping deal to a different week (updates submitted_date)
  const handleWeekDrop = async (dealId, newWeek) => {
    try {
      // Calculate new date based on week
      const [year, monthNum] = month.split('-');
      let day;
      switch(newWeek) {
        case 1: day = 3; break;  // Middle of week 1
        case 2: day = 10; break; // Middle of week 2
        case 3: day = 17; break; // Middle of week 3
        case 4: day = 25; break; // Middle of week 4
        default: day = 1;
      }
      const newDate = `${year}-${monthNum}-${String(day).padStart(2, '0')}`;
      
      await fetch(`${backendUrl}/api/gameplan/deals/${dealId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ submitted_date: newDate })
      });
      onRefresh();
      toast.success(`Moved to Week ${newWeek}`);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Handle status change from dropdown
  const handleStatusChange = async (dealId, newStatus) => {
    try {
      await fetch(`${backendUrl}/api/gameplan/deals/${dealId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status_stage: newStatus })
      });
      onRefresh();
      toast.success(`Status updated to ${getStageLabel(newStatus)}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleAddDeal = async (data) => {
    try {
      await fetch(`${backendUrl}/api/gameplan/deals`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      onRefresh();
      // If the chosen submitted_date isn't in the currently-viewed month,
      // tell the user explicitly so a "missing deal" doesn't confuse them.
      const dealMonth = (data.submitted_date || '').slice(0, 7);
      if (dealMonth && dealMonth !== month) {
        toast.success(`Deal added to ${dealMonth} board (switch month to view it)`);
      } else {
        toast.success('Deal added');
      }
    } catch (err) {
      toast.error('Failed to add deal');
    }
  };

  const handleDeleteDeal = async (dealId, clientName) => {
    // Guard: confirm first so accidents don't nuke data
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${clientName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/gameplan/deals/${dealId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onRefresh();
      toast.success(`"${clientName}" deleted`);
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`);
    }
  };

  return (
    <>
      <PremiumBoard
        month={month}
        boardData={boardData}
        live={live}
        viewMode={viewMode}
        setViewMode={setViewMode}
        authToken={authToken}
        refreshSignal={month}
        readOnly={readOnly}
        onOpenFile={onOpenFile}
        onAddDealTop={readOnly ? undefined : () => { setPresetWeek(null); setShowAddDialog(true); }}
        onAddDealToWeek={readOnly ? undefined : (w) => { setPresetWeek(w); setShowAddDialog(true); }}
        onImport={readOnly ? undefined : () => setShowImportDialog(true)}
        onDeleteDeal={readOnly ? undefined : handleDeleteDeal}
        onEditDeal={readOnly ? undefined : onEditDeal}
        onActivitySaved={onRefresh}
        onStage={onStage}
        boardOnly={boardOnly}
      />
      <AddDealDialog open={showAddDialog} onClose={() => { setShowAddDialog(false); setPresetWeek(null); }} onAdd={handleAddDeal} options={options} month={month} presetWeek={presetWeek} />
      <ImportCsvDialog open={showImportDialog} onClose={() => setShowImportDialog(false)} onImported={onRefresh} authToken={authToken} />
    </>
  );
};

export { BoardView };
export default BoardView;
